import InvoiceSimplifiedDTO from "features/invoices/dtos/InvoiceSimplifiedDTO";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";
import FormGroup from "features/shared/forms/components/FormGroup";
import React from "react";
import Select, { components, SingleValueProps } from "react-select";
import reactSelectStyles from "features/shared/components/reactSelect/reactselectcustom.module.scss";
import { useTranslation } from "react-i18next";
import { InvoiceSubType } from "../enums/invoiceSubType";
import { CommodityDetail } from "features/contracts/enums/commodityDetail";

interface Props {
  name: string;
  invoices: InvoiceSimplifiedDTO[];
  onChange: (invoice: InvoiceSimplifiedDTO | null) => void;
  label?: string;
  placeholder?: string;
  errors?: Record<string, string>;
}

const InvoiceSelect = ({
  name,
  invoices,
  onChange,
  label,
  placeholder,
  errors,
}: Props) => {
  // translations
  const { t } = useTranslation("invoice\\invoice");
  const { t: tInvoiceType } = useTranslation("invoice\\enums\\invoiceType");

  // helpers
  const isGeneralInvoice = (invoice: InvoiceSimplifiedDTO): boolean =>
    invoice.invoiceSubType === InvoiceSubType.GeneralInvoice ||
    invoice.invoiceSubType === InvoiceSubType.CorrectedGeneralInvoice;

  const renderCommodityIcons = (commodityDetails: CommodityDetail[]) => (
    <div className="d-flex align-items-center">
      {commodityDetails.map((detail, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="mx-1 text-muted">/</span>}
          <CommodityIcon commodityDetail={detail} includeColor size="lg" />
        </React.Fragment>
      ))}
    </div>
  );

  // sub-renders
  const renderOption = (invoice: InvoiceSimplifiedDTO) => {
    const commodity = invoice.commodityDetail?.[0];
    const from = invoice.timePeriodFrom ? new Date(invoice.timePeriodFrom).toLocaleDateString() : null;
    const to = invoice.timePeriodTo ? new Date(invoice.timePeriodTo).toLocaleDateString() : null;
    const general = isGeneralInvoice(invoice);
    return (
      <div className="d-flex align-items-center">
        {general
          ? invoice.commodityDetail && invoice.commodityDetail.length > 0 && renderCommodityIcons(invoice.commodityDetail)
          : commodity && <CommodityIcon commodityDetail={commodity} includeColor size="lg" />
        }
        <div className="pl-2" style={{ minWidth: 0 }}>
          <div className="text-truncate">
            <span>{invoice.invoiceNumber}</span>
            {invoice.invoiceType && (
              <>
                <span className="mx-2 text-muted">|</span>
                <span className="text-muted">{tInvoiceType(invoice.invoiceType)}</span>
              </>
            )}
          </div>
          {!general && from && to && (
            <div className="text-truncate mt-n1">
              <span className="small text-muted">{from} – {to}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSingleValue = (props: SingleValueProps<InvoiceSimplifiedDTO>) => (
    <components.SingleValue {...props}>
      {props.data.invoiceNumber ?? props.data.invoiceId}
    </components.SingleValue>
  );

  // render
  return (
    <FormGroup
      labelText={label ?? t("invoiceSelect.label")}
      validationError={errors ? errors[name] : undefined}
    >
      <Select
        options={invoices}
        getOptionLabel={(invoice) => invoice.invoiceNumber ?? invoice.invoiceId ?? ""}
        getOptionValue={(invoice) => invoice.invoiceId ?? ""}
        onChange={(val) => onChange(val)}
        formatOptionLabel={(invoice) => renderOption(invoice)}
        components={{ SingleValue: renderSingleValue }}
        captureMenuScroll={false}
        className={reactSelectStyles.customSelect}
        classNamePrefix="customSelect"
        placeholder={placeholder ?? t("invoiceSelect.placeholder")}
        noOptionsMessage={() => t("invoiceSelect.noOptions")}
      />
    </FormGroup>
  );
};

export default InvoiceSelect;
